import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { UserPreferencesProvider } from "@/lib/userPrefs";
import { EffortFormatProvider } from "@/lib/effortFormat";
import { ThemeProvider } from "@/lib/theme";
import ErrorBoundary from "@/components/ErrorBoundary";
import api from "@/lib/api";
import PermGate from "@/components/PermGate";
import Questionnaires from "@/pages/app/Questionnaires";
import RequirementBriefDetail from "@/pages/app/RequirementBriefDetail";
import RequirementBriefTemplates from "@/pages/app/RequirementBriefTemplates";
import PublicRequirementBrief from "@/pages/public/PublicRequirementBrief";
import { PublicExpoTickets, PublicTicketDetail } from "@/pages/public/PublicExpoTickets";
import PublicExpoLanding from "@/pages/public/PublicExpoLanding";

// LMS + IDP · Feb 2026 R9 / R10
import LmsCourses from "@/pages/app/lms/LmsCourses";
import LmsCourseDetail from "@/pages/app/lms/LmsCourseDetail";
import LmsMyLearning from "@/pages/app/lms/LmsMyLearning";
import LmsLearnCourse from "@/pages/app/lms/LmsLearnCourse";
import LmsTakeQuiz from "@/pages/app/lms/LmsTakeQuiz";
import LmsQuizResult from "@/pages/app/lms/LmsQuizResult";
import LmsStandaloneQuizzes from "@/pages/app/lms/LmsStandaloneQuizzes";
import LmsStandaloneQuizDetail from "@/pages/app/lms/LmsStandaloneQuizDetail";
import LmsMyQuizzes from "@/pages/app/lms/LmsMyQuizzes";
import LmsSettings from "@/pages/app/lms/LmsSettings";
import HiringHub from "@/pages/app/lms/HiringHub";
import HiringAssessmentDetail from "@/pages/app/lms/HiringAssessmentDetail";
import ExhibitionsList from "@/pages/app/exhibitions/ExhibitionsList";
import ExhibitionDetail from "@/pages/app/exhibitions/ExhibitionDetail";
import ExpoOrganizer from "@/pages/app/expo_organizer/ExpoOrganizer";
import DiagramsList from "@/pages/app/diagrams/DiagramsList";
import DiagramEditor from "@/pages/app/diagrams/DiagramEditor";
import DocumentsList from "@/pages/app/documents/DocumentsList";
import DocumentEditor from "@/pages/app/documents/DocumentEditor";
import SalesPerfDashboard from "@/pages/app/SalesPerfDashboard";
import PublicDiagramView from "@/pages/public/PublicDiagramView";
import PublicQuiz from "@/pages/public/PublicQuiz";
import IdpList from "@/pages/app/idp/IdpList";
import IdpDetail from "@/pages/app/idp/IdpDetail";

import MarketingHome from "@/pages/marketing/Home";
import Pricing from "@/pages/marketing/Pricing";
import Features from "@/pages/marketing/Features";
import Solutions from "@/pages/marketing/Solutions";
import Security from "@/pages/marketing/Security";
import LegalPage from "@/pages/marketing/Legal";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import SetPassword from "@/pages/auth/SetPassword";
import Onboarding from "@/pages/auth/Onboarding";
import BrandCompareShare from "@/pages/public/BrandCompareShare";
import PublicGtmBriefing from "@/pages/public/PublicGtmBriefing";

import AppShell from "@/components/Layout/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import BulletinBoard from "@/pages/app/BulletinBoard";
import LandingRedirect from "@/pages/app/LandingRedirect";
import PoliciesPage, { PolicyDetail } from "@/pages/app/Policies";
import EngagementAdmin from "@/pages/app/EngagementAdmin";
import CompanyQAPage from "@/pages/app/CompanyQA";
import Departments from "@/pages/app/Departments";
import SOPs from "@/pages/app/SOPs";
import Vault from "@/pages/app/Vault";
import Calendar from "@/pages/app/Calendar";
import UserManagement from "@/pages/app/UserManagement";
import Clients from "@/pages/app/Clients";
import ClientDetail from "@/pages/app/ClientDetail";
import CustomerOverview from "@/pages/app/CustomerOverview";
import Brands from "@/pages/app/Brands";
import BrandDetail from "@/pages/app/BrandDetail";
import Packages from "@/pages/app/Packages";
import PackageDetail from "@/pages/app/PackageDetail";
import ContentCalendar from "@/pages/app/ContentCalendar";
import ContentSheet from "@/pages/app/ContentSheet";
import ContentSheetPublic from "@/pages/public/ContentSheetPublic";
import DeliveryIntelligence from "@/pages/app/DeliveryIntelligence";
import PublicReport from "@/pages/app/PublicReport";
import SharedPackageView from "@/pages/SharedPackageView";
import Projects from "@/pages/app/Projects";
import ProjectDetail from "@/pages/app/ProjectDetail";
import ReviewItem from "@/pages/app/ReviewItem";
import Tasks from "@/pages/app/Tasks";
import PlanningBoard from "@/pages/app/PlanningBoard";
import TaskIntelligence from "@/pages/app/TaskIntelligence";
import Leaderboard from "@/pages/app/Leaderboard";
import PointsHistory from "@/pages/app/PointsHistory";
import PublicContract from "@/pages/PublicContract";
import PublicDocumentShare from "@/pages/public/PublicDocumentShare";
import Influencers from "@/pages/app/influencers/Influencers";
import InfluencerDetail from "@/pages/app/influencers/InfluencerDetail";
import InfluencerCampaigns from "@/pages/app/influencers/InfluencerCampaigns";
import InfluencerCampaignDetail from "@/pages/app/influencers/InfluencerCampaignDetail";
import InfluencerPayments from "@/pages/app/influencers/InfluencerPayments";
import Timesheet from "@/pages/app/Timesheet";
import Attendance from "@/pages/app/Attendance";
import Compliance from "@/pages/app/Compliance";
import HrDashboard from "@/pages/app/HrDashboard";
import HrOnboarding from "@/pages/app/HrOnboarding";
import BuddyHub from "@/pages/app/BuddyHub";
import HelpdeskInbox, { HelpdeskTicketDetail, HelpdeskSettings } from "@/pages/app/Helpdesk";
import HelpdeskIntelligence from "@/pages/app/HelpdeskIntelligence";
import HelpdeskClientPortal from "@/pages/public/HelpdeskClientPortal";
import PublicForm from "@/pages/public/PublicForm";
import HelpdeskClientTicketMagic from "@/pages/public/HelpdeskClientTicketMagic";
import HelpdeskKbBrowse from "@/pages/public/HelpdeskKbBrowse";
import AlumniPortal from "@/pages/app/AlumniPortal";
import PreJoiningPortal from "@/pages/PreJoiningPortal";
import PeopleAnalytics from "@/pages/app/PeopleAnalytics";
import QualityReview from "@/pages/app/QualityReview";
import Roles from "@/pages/app/Roles";
import HRDesignations from "@/pages/app/hr/HRDesignations";
import JobDutiesPage from "@/pages/app/hr/JobDutiesPage";
import HRProfileEditsAdmin from "@/pages/app/hr/HRProfileEditsAdmin";
import HRCompensation from "@/pages/app/HRCompensation";
import AllMeetings from "@/pages/app/AllMeetings";
import TeamAccessMatrix from "@/pages/app/TeamAccessMatrix";
import TeamAllocation from "@/pages/app/TeamAllocation";
import AllActionItems from "@/pages/app/AllActionItems";
import MeetingPage from "@/pages/app/MeetingPage";
import Team from "@/pages/app/Team";
import Billing from "@/pages/app/Billing";
import TaskBillingDashboardPage from "@/pages/app/TaskBillingDashboardPage";
import BillingSuccess from "@/pages/app/BillingSuccess";
import Referrals from "@/pages/app/Referrals";
import Storage from "@/pages/app/Storage";
import Settings from "@/pages/app/Settings";
import SchedulerSettings from "@/pages/app/settings/SchedulerSettings";
import TaskCategoriesSettings from "@/pages/app/settings/TaskCategoriesSettings";
import BrandKitsSettings from "@/pages/app/settings/BrandKitsSettings";
import MailProfilesSettings from "@/pages/app/settings/MailProfilesSettings";
import DeveloperSettings from "@/pages/app/settings/DeveloperSettings";
import DeveloperWebhooks from "@/pages/app/settings/DeveloperWebhooks";
import DeveloperApiDocs from "@/pages/app/settings/DeveloperApiDocs";
import ProjectStatusesSettings from "@/pages/app/settings/ProjectStatusesSettings";
import ProjectPeopleLabelsSettings from "@/pages/app/settings/ProjectPeopleLabelsSettings";
import ModulesSettings from "@/pages/app/settings/ModulesSettings";
import Integrations from "@/pages/app/Integrations";
import Inbox from "@/pages/app/Inbox";
import Webmail from "@/pages/app/Webmail";
import Escalations from "@/pages/app/Escalations";
import AutoResponse from "@/pages/app/AutoResponse";
import Profile from "@/pages/app/Profile";
import Notifications from "@/pages/app/Notifications";
import Log from "@/pages/app/Log";
import LogDashboard from "@/pages/app/LogDashboard";
import SEO from "@/pages/app/SEO";
import PerformanceHub from "@/pages/app/performance/PerformanceHub";
import PerformanceAccounts from "@/pages/app/performance/PerformanceAccounts";
import PerformanceCampaigns from "@/pages/app/performance/PerformanceCampaigns";
import PerformanceReports from "@/pages/app/performance/PerformanceReports";
import PerformanceAlerts from "@/pages/app/performance/PerformanceAlerts";
import MetaSetupWizard from "@/pages/app/performance/MetaSetupWizard";
import MetaLeadAds from "@/pages/app/performance/MetaLeadAds";
import PerformanceCampaignDetail from "@/pages/app/performance/PerformanceCampaignDetail";
import MultiBrandDashboard from "@/pages/app/performance/MultiBrandDashboard";
import ClientPerformanceRollup from "@/pages/app/performance/ClientPerformanceRollup";
import PerformanceAttribution from "@/pages/app/performance/PerformanceAttribution";
import WebhooksOutbox from "@/pages/app/webhooks/WebhooksOutbox";
import FlowsList from "@/pages/app/flows/FlowsList";
import ProjectIntelligence from "@/pages/app/ProjectIntelligence";
import Employees from "@/pages/app/Employees";
import EmployeesDashboard from "@/pages/app/EmployeesDashboard";
import PIPAdmin from "@/pages/app/PIPAdmin";
import OrgChart from "@/pages/app/OrgChart";
import NowDashboard from "@/pages/app/NowDashboard";
import EmployeeDetail from "@/pages/app/EmployeeDetail";
import Leads from "@/pages/app/Leads";
import Sales from "@/pages/app/Sales";
import GstSales from "@/pages/app/GstSales";  // R268 Feb 2026 · Indian GST/TDS lifecycle
import BillingSetup from "@/pages/app/BillingSetup";  // R268q Feb 2026 · Org billing profile + PDF template setup
import SalesCommandCenter from "@/pages/app/SalesCommandCenter";
import DunningQueue from "@/pages/app/DunningQueue";
import ActivityFeed from "@/pages/app/ActivityFeed";
import MyDashboard from "@/pages/app/MyDashboard";
import MyJourney from "@/pages/app/MyJourney";
import TemplateGallery from "@/pages/app/hr/TemplateGallery";
import ApprovalsInbox from "@/pages/app/hr/ApprovalsInbox";
import ReplanningDashboard from "@/pages/app/ReplanningDashboard";
import TimesheetMovesAudit from "@/pages/app/TimesheetMovesAudit";
import CompOffRules from "@/pages/app/CompOffRules";
import HrCorrections from "@/pages/app/HrCorrections";
import TeamReminders from "@/pages/app/TeamReminders";
import RemindersIntelReport from "@/pages/app/RemindersIntelReport";
import RFQList from "@/pages/app/estimates/RFQList";
import RFQDetail from "@/pages/app/estimates/RFQDetail";
import EstimateList from "@/pages/app/estimates/EstimateList";
import EstimateDetail from "@/pages/app/estimates/EstimateDetail";
import VarianceDashboard from "@/pages/app/estimates/VarianceDashboard";
import ContractList from "@/pages/app/contracts/ContractList";
import ContractDetail from "@/pages/app/contracts/ContractDetail";
import DeliveryBoard from "@/pages/app/estimates/DeliveryBoard";
import ProfitabilityDashboard from "@/pages/app/estimates/ProfitabilityDashboard";
import LineAnalytics from "@/pages/app/estimates/LineAnalytics";
import ContractTemplates from "@/pages/app/estimates/ContractTemplates";
import ClientRfqPortal from "@/pages/public/ClientRfqPortal";
import SignaturePage from "@/pages/public/SignaturePage";
import OnboardingKits from "@/pages/app/OnboardingKits";
import Calls from "@/pages/app/Calls";
import Huddles from "@/pages/app/Huddles";
import DataImport from "@/pages/app/DataImport";
import LeadsImportHistory from "@/pages/app/LeadsImportHistory";
import Accounts from "@/pages/app/Accounts";
import LeadsMergeReview from "@/pages/app/LeadsMergeReview";
import FormsPage from "@/pages/app/Forms";
import FormBuilder from "@/pages/app/FormBuilder";
import FormMergeQueue from "@/pages/app/FormMergeQueue";
import StudioBooking from "@/pages/app/StudioBooking";
import StudioScan from "@/pages/app/StudioScan";
import StudioSpaceDetail from "@/pages/app/StudioSpaceDetail";
import StudioBookingWidget from "@/pages/public/StudioBookingWidget";
import StudioBookingTracker from "@/pages/public/StudioBookingTracker";
import EmailSubscriptionPage from "@/pages/public/EmailSubscriptionPage";
import PromoShareLandingRedirect from "@/pages/public/PromoShareLandingRedirect";
import PaymentReturn from "@/pages/public/PaymentReturn";
import EnquiryWidget from "@/pages/public/EnquiryWidget";
import EnquiryWidgetSlug from "@/pages/public/EnquiryWidgetSlug";
import EnquiryPortal from "@/pages/public/EnquiryPortal";
import EnquiriesPage from "@/pages/app/EnquiriesPage";
import PublicShootDelivery from "@/pages/public/PublicShootDelivery";
import PublicQuestionnaire from "@/pages/public/PublicQuestionnaire";
import SkillCenter from "@/pages/app/SkillCenter";
import WorkspaceMap from "@/pages/app/WorkspaceMap";
import Assets from "@/pages/app/Assets";
import Goals from "@/pages/app/Goals";
import Prospecting from "@/pages/app/Prospecting";
import VoiceAgents from "@/pages/app/VoiceAgents";
import VoiceCampaigns from "@/pages/app/VoiceCampaigns";
import TasksDashboard from "@/pages/app/TasksDashboard";
import Marketing from "@/pages/app/Marketing";
import Campaigns from "@/pages/app/Campaigns";
import Suppressions from "@/pages/app/Suppressions";import WaInbox from "@/pages/app/WaInbox";
import Trends from "@/pages/app/Trends";
import CampaignPlaybooks from "@/pages/app/CampaignPlaybooks";
import BrandCockpit from "@/pages/app/BrandCockpit";
import GrowthCockpit from "@/pages/app/GrowthCockpit";
import BizDev from "@/pages/app/BizDev";
import RevenueFlow from "@/pages/app/RevenueFlow";
import MsmeEssentials from "@/pages/app/MsmeEssentials";
import Finance from "@/pages/app/Finance";
import FinanceHub from "@/pages/app/FinanceHub";  // R268y · Finance dashboard hub
import FinanceVendors from "@/pages/app/FinanceVendors";
import FinanceProjectSpend from "@/pages/app/FinanceProjectSpend";  // Feb 2026 · Project Spend rollup
import FinanceReports from "@/pages/app/FinanceReports";
import FinanceCopilot from "@/pages/app/FinanceCopilot";
import ScenarioPlanner from "@/pages/app/ScenarioPlanner";
import CommsCenter from "@/pages/app/CommsCenter";
import CommsBranding from "@/pages/app/CommsBranding";
import FinanceAssets from "@/pages/app/FinanceAssets";
import FinanceClose from "@/pages/app/FinanceClose";
import FinanceCollections from "@/pages/app/FinanceCollections";
import FinanceLeadership from "@/pages/app/FinanceLeadership";
import FinancePayRun from "@/pages/app/FinancePayRun";
import Assignments from "@/pages/app/Assignments";
import MarketableDays from "@/pages/app/MarketableDays";
import FeedbackRequests from "@/pages/app/FeedbackRequests";
import FeedbackNPSReport from "@/pages/app/FeedbackNPSReport";
import SentimentTrends from "@/pages/app/SentimentTrends";
import WaTemplateLibrary from "@/pages/app/WaTemplateLibrary";
import PublicFeedback from "@/pages/PublicFeedback";
import Portfolios from "@/pages/app/Portfolios";
import PortfolioDetail from "@/pages/app/PortfolioDetail";
import ItemCatalog from "@/pages/app/ItemCatalog";

import AdminShell from "@/components/Layout/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminWorkspaces from "@/pages/admin/AdminWorkspaces";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminPaymentGateway from "@/pages/admin/AdminPaymentGateway";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AccountSecurity from "@/pages/app/AccountSecurity";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import AdminAiUsage from "@/pages/admin/AdminAiUsage";
import AdminDataIntegrity from "@/pages/admin/AdminDataIntegrity";
import AdminPerfStats from "@/pages/admin/AdminPerfStats";
import AdminChromeExtension from "@/pages/admin/AdminChromeExtension";
import ExtAuthorize from "@/pages/ExtAuthorize";
import ReportsDailyCompletion from "@/pages/app/ReportsDailyCompletion";
import ComplianceMonitor from "@/pages/app/ComplianceMonitor";
import ComplianceStreaks from "@/pages/app/ComplianceStreaks";
import ReportsTimesheetAdvanced from "@/pages/app/ReportsTimesheetAdvanced";

import ReviewLink from "@/pages/public/ReviewLink";
import EmbedReview from "@/pages/public/EmbedReview";
import ClientPortal from "@/pages/public/ClientPortal";
import Cert from "@/pages/public/Cert";
import VerifyEmail from "@/pages/auth/VerifyEmail";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/app" replace />;
  return children;
}

// Feb 2026 R9 · Redirects to the current user's most recent IDP or the
// blank state if they don't have one yet.
function MyIdpRedirect() {
  const { activeWorkspaceId } = useAuth();
  const [state, setState] = useState({ loading: true, planId: null });
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/idp/me`)
      .then(({ data }) => setState({ loading: false, planId: data?.[0]?.id }))
      .catch(() => setState({ loading: false, planId: null }));
  }, [activeWorkspaceId]);
  if (state.loading) return <div className="p-6">Loading your IDP…</div>;
  if (state.planId) return <Navigate to={`/app/idp/${state.planId}`} replace />;
  return (
    <div className="p-6 max-w-lg mx-auto text-center border border-dashed border-border">
      <h1 className="text-lg font-semibold mb-1">No IDP assigned yet</h1>
      <p className="text-sm text-muted-foreground">Ask your manager or HR to create your Individual Development Plan.</p>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <UserPreferencesProvider>
          <EffortFormatProvider>
          <BrowserRouter>
          <Routes>
            {/* Marketing */}
            <Route path="/" element={<MarketingHome />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/security" element={<Security />} />
            <Route path="/legal/:slug" element={<LegalPage />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/report/:token" element={<PublicReport />} />
            <Route path="/pkg/view/:token" element={<SharedPackageView />} />
            <Route path="/welcome/:token" element={<PreJoiningPortal />} />
            <Route path="/q/:token" element={<PublicQuestionnaire />} />
            <Route path="/rb/:token" element={<PublicRequirementBrief />} />
            <Route path="/pq/:token" element={<PublicQuiz />} />
            <Route path="/public/expo/:expoId/tickets" element={<PublicExpoTickets />} />
            <Route path="/public/expo/tickets/:qrToken" element={<PublicTicketDetail />} />
            <Route path="/public/expo/:expoId" element={<PublicExpoLanding />} />
            <Route path="/diagram/:token" element={<PublicDiagramView />} />
            <Route path="/compare/:token" element={<BrandCompareShare />} />
            <Route path="/gtm/share/:token" element={<PublicGtmBriefing />} />
            <Route path="/share/content-sheet/:token" element={<ContentSheetPublic />} />
            <Route path="/contract/:token" element={<PublicContract />} />
            <Route path="/share/:token" element={<PublicDocumentShare />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />

            {/* Public review link + certificate */}
            <Route path="/r/:token" element={<ReviewLink />} />
            <Route path="/embed/r/:token" element={<EmbedReview />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route path="/feedback/:token" element={<PublicFeedback />} />
            <Route path="/client/portal" element={<HelpdeskClientPortal />} />
            <Route path="/client/ticket/:token" element={<HelpdeskClientTicketMagic />} />
            <Route path="/client/rfq/:token" element={<ClientRfqPortal />} />
            <Route path="/sign/:token" element={<SignaturePage />} />
            <Route path="/help/:snippetKey" element={<HelpdeskKbBrowse />} />
            <Route path="/cert/:certNo" element={<Cert />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Feb 2026 · Public lead-capture form (from form builder) */}
            <Route path="/f/:formId" element={<PublicForm />} />
            {/* Feb 2026 · Public studio booking widget */}
            <Route path="/book/:wsId" element={<StudioBookingWidget />} />
            {/* Feb 2026 · R280 · Public booking status tracker (linked from every txnl email) */}
            <Route path="/track/:wsId/:ref" element={<StudioBookingTracker />} />
            {/* Feb 2026 · CAN-SPAM compliance — public unsubscribe + preferences page */}
            <Route path="/email/unsubscribe" element={<EmailSubscriptionPage mode="unsubscribe" />} />
            <Route path="/email/preferences" element={<EmailSubscriptionPage mode="preferences" />} />
            {/* Feb 2026 · Promo short-link redirect → resolves slug to /book?promo=&sl= */}
            <Route path="/b/:slug" element={<PromoShareLandingRedirect />} />
            {/* Feb 2026 · Cashfree hosted-checkout return page */}
            <Route path="/pay/return" element={<PaymentReturn />} />
            {/* Feb 2026 · Public enquiries widget */}
            <Route path="/enquire/:wsId" element={<EnquiryWidget />} />
            <Route path="/enquire/:wsId/:typeSlug" element={<EnquiryWidget />} />
            {/* Feb 2026 · Public embeddable multi-type enquiry widget */}
            <Route path="/enquire-w/:slug" element={<EnquiryWidgetSlug />} />
            {/* Feb 2026 · Public enquiry customer portal (linked from auto-reply) */}
            <Route path="/e/:code" element={<EnquiryPortal />} />
            {/* Feb 2026 · Public shoot delivery sign-off */}
            <Route path="/deliver/:token" element={<PublicShootDelivery />} />

            {/* App */}
            <Route
              path="/app"
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route index element={<LandingRedirect />} />
              <Route path="home" element={<BulletinBoard />} />
              <Route path="dashboard-classic" element={<Dashboard />} />
              <Route path="departments" element={<PermGate anyOf={["department.manage", "employee.view", "employee.manage"]}><Departments /></PermGate>} />
              <Route path="sops" element={<PermGate anyOf={["sop.view", "sop.run", "sop.manage"]}><SOPs /></PermGate>} />
              <Route path="vault" element={<PermGate anyOf={["vault.view", "vault.manage"]}><Vault /></PermGate>} />
              <Route path="calendar" element={<PermGate anyOf={["calendar.view", "calendar.manage"]}><Calendar /></PermGate>} />
              <Route path="users" element={<PermGate anyOf={["workspace.member.manage", "workspace.member.invite", "hr.manage"]}><UserManagement /></PermGate>} />
              <Route path="requirement-briefs" element={<PermGate anyOf={["quotation.view", "quotation.manage", "questionnaire.view", "questionnaire.manage"]}><Questionnaires /></PermGate>} />
              <Route path="requirement-briefs/templates" element={<PermGate anyOf={["quotation.view", "quotation.manage", "questionnaire.view", "questionnaire.manage"]}><RequirementBriefTemplates /></PermGate>} />
              <Route path="requirement-briefs/:id" element={<PermGate anyOf={["quotation.view", "quotation.manage", "questionnaire.view", "questionnaire.manage"]}><RequirementBriefDetail /></PermGate>} />
              {/* LMS + IDP · Feb 2026 R9 */}
              <Route path="lms/courses" element={<PermGate anyOf={["lms.view", "lms.manage", "workspace.admin", "hr.manage", "workspace.view"]}><LmsCourses /></PermGate>} />
              <Route path="lms/courses/:id" element={<PermGate anyOf={["lms.view", "lms.manage", "workspace.admin", "hr.manage", "workspace.view"]}><LmsCourseDetail /></PermGate>} />
              <Route path="lms/standalone-quizzes" element={<PermGate anyOf={["lms.manage", "workspace.admin", "hr.manage"]}><LmsStandaloneQuizzes /></PermGate>} />
              <Route path="lms/standalone-quizzes/:id" element={<PermGate anyOf={["lms.manage", "workspace.admin", "hr.manage"]}><LmsStandaloneQuizDetail /></PermGate>} />
              <Route path="lms/settings" element={<PermGate anyOf={["lms.manage", "workspace.admin", "hr.manage"]}><LmsSettings /></PermGate>} />
              <Route path="lms/hiring" element={<PermGate anyOf={["lms.manage", "workspace.admin", "hr.manage", "hiring.manage"]}><HiringHub /></PermGate>} />
              <Route path="lms/hiring/:id" element={<PermGate anyOf={["lms.manage", "workspace.admin", "hr.manage", "hiring.manage", "hiring.view"]}><HiringAssessmentDetail /></PermGate>} />
              <Route path="exhibitions" element={<PermGate anyOf={["sales.view", "sales.manage", "marketing.view", "marketing.manage", "workspace.admin", "exhibitions.view", "exhibitions.manage"]}><ExhibitionsList /></PermGate>} />
              <Route path="exhibitions/:id" element={<PermGate anyOf={["sales.view", "sales.manage", "marketing.view", "marketing.manage", "workspace.admin", "exhibitions.view", "exhibitions.manage"]}><ExhibitionDetail /></PermGate>} />
              <Route path="expo-organizer/*" element={<PermGate anyOf={["sales.view", "sales.manage", "marketing.view", "marketing.manage", "workspace.admin", "organizer.view", "organizer.manage"]}><ExpoOrganizer /></PermGate>} />
              {/* Diagrams · Iter Feb 2026 R39 */}
              <Route path="diagrams" element={<DiagramsList />} />
              <Route path="diagrams/:id" element={<DiagramEditor />} />
              <Route path="documents" element={<DocumentsList />} />
              <Route path="documents/:id" element={<DocumentEditor />} />
              <Route path="sales-perf" element={<SalesPerfDashboard />} />
              <Route path="lms/my-quizzes" element={<LmsMyQuizzes />} />
              <Route path="lms/my-learning" element={<LmsMyLearning />} />
              <Route path="lms/learn/:courseId" element={<LmsLearnCourse />} />
              <Route path="lms/quizzes/:quizId/take" element={<LmsTakeQuiz />} />
              <Route path="lms/attempts/:attemptId/result" element={<LmsQuizResult />} />
              <Route path="idp" element={<PermGate anyOf={["idp.manage", "hr.manage", "workspace.admin"]}><IdpList /></PermGate>} />
              <Route path="idp/:id" element={<IdpDetail />} />
              <Route path="idp/me" element={<MyIdpRedirect />} />
              <Route path="timesheet" element={<Timesheet />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="my-attendance" element={<Attendance />} />
              <Route path="compliance" element={<PermGate anyOf={["hr.approve", "hr.manage", "timesheet.report"]}><Compliance /></PermGate>} />
              <Route path="hr-dashboard" element={<PermGate anyOf={["hr.approve", "hr.manage", "timesheet.report", "employee.manage"]}><HrDashboard /></PermGate>} />
              <Route path="hr/onboarding" element={<PermGate anyOf={["hr.approve", "hr.manage", "employee.manage"]}><HrOnboarding /></PermGate>} />
              <Route path="hr/onboarding/:jid" element={<PermGate anyOf={["hr.approve", "hr.manage", "employee.manage"]}><HrOnboarding /></PermGate>} />
              <Route path="buddy" element={<BuddyHub />} />
              <Route path="helpdesk" element={<PermGate anyOf={["helpdesk.view", "helpdesk.reply", "helpdesk.manage"]}><HelpdeskInbox /></PermGate>} />
              <Route path="helpdesk/settings" element={<PermGate anyOf={["helpdesk.settings"]}><HelpdeskSettings /></PermGate>} />
              <Route path="helpdesk/intelligence" element={<PermGate anyOf={["helpdesk.view", "helpdesk.settings"]}><HelpdeskIntelligence /></PermGate>} />
              <Route path="helpdesk/tickets/:tid" element={<PermGate anyOf={["helpdesk.view", "helpdesk.reply", "helpdesk.manage"]}><HelpdeskTicketDetail /></PermGate>} />
            <Route path="policies" element={<PermGate anyOf={["policy.view", "policy.manage"]}><PoliciesPage /></PermGate>} />
            <Route path="policies/:pid" element={<PermGate anyOf={["policy.view", "policy.manage"]}><PolicyDetail /></PermGate>} />
            <Route path="engagement" element={<PermGate anyOf={["engagement.view", "engagement.manage", "engagement.analytics"]}><EngagementAdmin /></PermGate>} />
            <Route path="company-qa" element={<PermGate anyOf={["qa.ask", "qa.manage"]}><CompanyQAPage /></PermGate>} />
              <Route path="alumni" element={<PermGate anyOf={["employee.view", "employee.manage"]}><AlumniPortal /></PermGate>} />
              <Route path="people" element={<PermGate anyOf={["employee.manage", "hr.approve", "hr.manage"]}><PeopleAnalytics /></PermGate>} />
              <Route path="clients" element={<PermGate anyOf={["client.view"]}><Clients /></PermGate>} />
              <Route path="clients/:cid" element={<PermGate anyOf={["client.view"]}><ClientDetail /></PermGate>} />
              <Route path="customers/:cid" element={<PermGate anyOf={["client.view"]}><CustomerOverview /></PermGate>} />
              <Route path="brands" element={<PermGate module="brands" anyOf={["brand.view", "brand.manage", "client.view"]}><Brands /></PermGate>} />
              <Route path="brands/:bid" element={<PermGate module="brands" anyOf={["brand.view", "brand.manage", "client.view"]}><BrandDetail /></PermGate>} />
              <Route path="packages" element={<PermGate anyOf={["package.view", "package.manage", "brand.view", "client.view"]}><Packages /></PermGate>} />
              <Route path="packages/:pid" element={<PermGate anyOf={["package.view", "package.manage", "brand.view", "client.view"]}><PackageDetail /></PermGate>} />
              <Route path="content-calendar" element={<PermGate anyOf={["package.view", "package.manage", "brand.view", "client.view"]}><ContentCalendar /></PermGate>} />
              <Route path="content-sheet" element={<PermGate anyOf={["content_sheet.view", "workspace.admin", "workspace.manage", "content_calendar.view_all"]}><ContentSheet /></PermGate>} />
              <Route path="delivery-intelligence" element={<PermGate anyOf={["package.view", "package.manage", "brand.view", "client.view"]}><DeliveryIntelligence /></PermGate>} />
              <Route path="projects" element={<Projects />} />
              <Route path="planning" element={<PlanningBoard />} />
              <Route path="projects/:pid" element={<ProjectDetail />} />
              <Route path="review-items/:iid" element={<ReviewItem />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="reports/daily-completion" element={<ReportsDailyCompletion />} />
              <Route path="reports/compliance-monitor" element={<ComplianceMonitor />} />
              <Route path="reports/compliance-monitor/streaks" element={<ComplianceStreaks />} />
              <Route path="reports/timesheet-advanced" element={<PermGate anyOf={["timesheet.report_view","timesheet.report_all","workspace.admin","workspace.manage"]}><ReportsTimesheetAdvanced /></PermGate>} />
              <Route path="task-intelligence" element={<PermGate anyOf={["task.intelligence.view"]}><TaskIntelligence /></PermGate>} />
              <Route path="leaderboard" element={<Leaderboard />} />
              <Route path="points-history" element={<PointsHistory />} />
              <Route path="influencers" element={<Influencers />} />
              <Route path="influencers/campaigns" element={<InfluencerCampaigns />} />
              <Route path="influencers/campaigns/:cid" element={<InfluencerCampaignDetail />} />
              <Route path="influencers/payments" element={<InfluencerPayments />} />
              <Route path="influencers/:iid" element={<InfluencerDetail />} />
              <Route path="quality-review" element={<QualityReview />} />
              <Route path="roles" element={<PermGate anyOf={["workspace.role.manage"]}><Roles /></PermGate>} />
              <Route path="me" element={<MyDashboard />} />
              <Route path="my/onboarding" element={<MyJourney />} />
              <Route path="my/journey" element={<MyJourney />} />
              <Route path="my-journey" element={<MyJourney />} />
              <Route path="hr/templates" element={<TemplateGallery />} />
              <Route path="my/approvals" element={<ApprovalsInbox />} />
              <Route path="replanning" element={<ReplanningDashboard />} />
              <Route path="timesheet/moves" element={<TimesheetMovesAudit />} />
              <Route path="hr/comp-off-rules" element={<CompOffRules />} />
              <Route path="hr/corrections" element={<HrCorrections />} />
              <Route path="hr/compensation" element={<PermGate anyOf={["hr.compensation.manage", "workspace.admin"]}><HRCompensation /></PermGate>} />
              <Route path="reminders/team" element={<TeamReminders />} />
              <Route path="reminders/reports" element={<RemindersIntelReport />} />
              <Route path="rfqs" element={<RFQList />} />
              <Route path="rfqs/:rid" element={<RFQDetail />} />
              <Route path="estimates" element={<EstimateList />} />
              <Route path="estimates/variance" element={<VarianceDashboard />} />
              <Route path="estimates/:eid" element={<EstimateDetail />} />
              <Route path="items" element={<ItemCatalog />} />
              <Route path="contracts" element={<ContractList />} />
              <Route path="contracts/:cid" element={<ContractDetail />} />
              <Route path="delivery" element={<DeliveryBoard />} />
              <Route path="profitability" element={<ProfitabilityDashboard />} />
              <Route path="line-analytics" element={<LineAnalytics />} />
              <Route path="contract-templates" element={<ContractTemplates />} />
              <Route path="onboarding-kits" element={<OnboardingKits />} />
              <Route path="calls" element={<Calls />} />
              <Route path="huddles" element={<Huddles />} />
              <Route path="import" element={<DataImport />} />
              <Route path="data-import" element={<DataImport />} />
              <Route path="skill-center" element={<PermGate anyOf={["skill.view_all", "skill.approve", "skill.manage_all", "employee.view"]}><SkillCenter /></PermGate>} />
              <Route path="map" element={<WorkspaceMap />} />
              <Route path="assets" element={<PermGate anyOf={["asset.view", "asset.manage", "asset.assign"]}><Assets /></PermGate>} />
              <Route path="goals" element={<PermGate anyOf={["goal.manage", "leaderboard.view"]}><Goals /></PermGate>} />
              <Route path="prospecting" element={<PermGate anyOf={["prospect.view"]}><Prospecting /></PermGate>} />
              <Route path="prospects" element={<PermGate anyOf={["prospect.view"]}><Prospecting /></PermGate>} />
              <Route path="contacts" element={<PermGate anyOf={["client.view", "prospect.view"]}><Prospecting /></PermGate>} />
              <Route path="stakeholders" element={<PermGate anyOf={["client.view"]}><Clients /></PermGate>} />
              <Route path="voice-agents" element={<PermGate anyOf={["marketing.view"]}><VoiceAgents /></PermGate>} />
              <Route path="voice-campaigns" element={<PermGate anyOf={["marketing.view", "crm.view"]}><VoiceCampaigns /></PermGate>} />
              <Route path="tasks-dashboard" element={<PermGate anyOf={["task.view", "task.manage"]}><TasksDashboard /></PermGate>} />
              <Route path="marketing" element={<PermGate anyOf={["marketing.view"]}><Marketing /></PermGate>} />
              <Route path="campaigns" element={<PermGate anyOf={["lead.view", "marketing.view"]}><Campaigns /></PermGate>} />
              <Route path="suppressions" element={<PermGate anyOf={["lead.view", "marketing.view"]}><Suppressions /></PermGate>} />
              <Route path="marketing/wa-inbox" element={<PermGate anyOf={["marketing.view"]}><WaInbox /></PermGate>} />
              <Route path="wa-inbox" element={<PermGate anyOf={["marketing.view"]}><WaInbox /></PermGate>} />
              <Route path="trends" element={<PermGate anyOf={["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view"]}><Trends /></PermGate>} />
              <Route path="marketing/playbooks" element={<PermGate anyOf={["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view"]}><CampaignPlaybooks /></PermGate>} />
              <Route path="brands/:brand_id/cockpit" element={<PermGate module="brands" anyOf={["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view"]}><BrandCockpit /></PermGate>} />
              <Route path="growth-cockpit" element={<PermGate anyOf={["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view", "prospect.view"]}><GrowthCockpit /></PermGate>} />
              <Route path="performance" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceHub /></PermGate>} />
              <Route path="performance/accounts" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceAccounts /></PermGate>} />
              <Route path="performance/campaigns" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceCampaigns /></PermGate>} />
              <Route path="performance/reports" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceReports /></PermGate>} />
              <Route path="performance/alerts" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceAlerts /></PermGate>} />
              <Route path="performance/meta-setup" element={<PermGate anyOf={["marketing.manage", "workspace.admin"]}><MetaSetupWizard /></PermGate>} />
              <Route path="performance/lead-ads" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><MetaLeadAds /></PermGate>} />
              <Route path="performance/campaigns/:cid" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceCampaignDetail /></PermGate>} />
              <Route path="performance/multi-brand" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><MultiBrandDashboard /></PermGate>} />
              <Route path="performance/clients/:cid" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><ClientPerformanceRollup /></PermGate>} />
              <Route path="performance/attribution" element={<PermGate anyOf={["marketing.view", "marketing.manage"]}><PerformanceAttribution /></PermGate>} />
              <Route path="webhooks" element={<PermGate anyOf={["workspace.admin", "workspace.manage"]}><WebhooksOutbox /></PermGate>} />
              <Route path="flows" element={<FlowsList />} />
              <Route path="project-intel" element={<ProjectIntelligence />} />
              <Route path="bizdev" element={<PermGate anyOf={["workspace.manage"]}><BizDev /></PermGate>} />
              <Route path="finance/revenue-flow" element={<PermGate anyOf={["finance.view", "finance.manage", "budget.view", "report.view", "invoice.view", "invoice.manage", "lead.view", "client.view"]}><RevenueFlow /></PermGate>} />
              <Route path="finance/msme" element={<PermGate anyOf={["finance.view", "finance.manage", "invoice.view", "invoice.manage"]}><MsmeEssentials /></PermGate>} />
              {/* R268y · /app/finance is now the Finance HUB (KPIs +
                  grouped shortcuts). The old bookkeeping page moved
                  to /app/finance/bookkeeping. */}
              <Route path="finance" element={<PermGate anyOf={["finance.view", "finance.manage", "budget.view", "report.view"]}><FinanceHub /></PermGate>} />
              <Route path="finance/bookkeeping" element={<PermGate anyOf={["finance.view", "finance.manage", "budget.view", "report.view"]}><Finance /></PermGate>} />
              <Route path="finance/receivables" element={<PermGate anyOf={["finance.view", "finance.manage", "invoice.view", "invoice.manage"]}><FinanceCollections /></PermGate>} />
              <Route path="finance/vendors" element={<PermGate anyOf={["finance.view", "finance.manage", "expense.view", "expense.manage"]}><FinanceVendors /></PermGate>} />
              <Route path="finance/project-spend" element={<PermGate anyOf={["finance.view", "finance.manage", "expense.view", "expense.manage"]}><FinanceProjectSpend /></PermGate>} />
              <Route path="finance/reports" element={<PermGate anyOf={["report.view", "finance.view", "finance.manage", "budget.view"]}><FinanceReports /></PermGate>} />
              <Route path="finance/copilot" element={<PermGate anyOf={["finance.view", "finance.manage"]}><FinanceCopilot /></PermGate>} />
              <Route path="finance/scenario-planner" element={<PermGate anyOf={["finance.view", "finance.manage", "budget.view", "budget.manage"]}><ScenarioPlanner /></PermGate>} />
              <Route path="admin/comms" element={<PermGate anyOf={["workspace.manage"]}><CommsCenter /></PermGate>} />
              <Route path="comms" element={<PermGate anyOf={["workspace.manage"]}><CommsCenter /></PermGate>} />
              <Route path="comms/branding" element={<PermGate anyOf={["workspace.manage"]}><CommsBranding /></PermGate>} />
              <Route path="finance/advanced" element={<PermGate anyOf={["finance.view", "finance.manage", "asset.view_finance", "asset.manage_finance"]}><FinanceAssets /></PermGate>} />
              <Route path="finance/close" element={<PermGate anyOf={["finance.view", "finance.manage"]}><FinanceClose /></PermGate>} />
              <Route path="finance/collections" element={<PermGate anyOf={["finance.view", "finance.manage", "invoice.view", "invoice.manage", "payment.view", "payment.manage"]}><FinanceCollections /></PermGate>} />
              <Route path="finance/leadership" element={<PermGate anyOf={["finance.view", "finance.manage", "report.view"]}><FinanceLeadership /></PermGate>} />
              <Route path="finance/pay-run" element={<PermGate anyOf={["finance.view", "finance.manage", "expense.view", "expense.manage"]}><FinancePayRun /></PermGate>} />
              <Route path="finance/task-billing" element={<PermGate anyOf={["finance.view", "finance.manage", "workspace.admin", "workspace.manage"]}><TaskBillingDashboardPage /></PermGate>} />
              <Route path="assignments" element={<Assignments />} />
              <Route path="marketing/marketable-days" element={<MarketableDays />} />
              <Route path="feedback" element={<FeedbackRequests />} />
              <Route path="feedback/nps" element={<FeedbackNPSReport />} />
              <Route path="feedback/sentiment-trends" element={<SentimentTrends />} />
              <Route path="wa-templates" element={<WaTemplateLibrary />} />
              <Route path="portfolios" element={<PermGate anyOf={["portfolio.view", "portfolio.manage", "workspace.manage"]}><Portfolios /></PermGate>} />
              <Route path="portfolios/:pid" element={<PermGate anyOf={["portfolio.view", "portfolio.manage", "workspace.manage"]}><PortfolioDetail /></PermGate>} />
              <Route path="meetings" element={<AllMeetings />} />
              <Route path="access/team-matrix" element={<TeamAccessMatrix />} />
              <Route path="access/allocation" element={<TeamAllocation />} />
              <Route path="meetings/:mid" element={<MeetingPage />} />
              <Route path="action-items" element={<AllActionItems />} />
              <Route path="team" element={<Team />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="brand-library" element={<Navigate to="/app/brands?tab=library" replace />} />
              <Route path="storage" element={<Storage />} />
              <Route path="activity" element={<ActivityFeed />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/scheduler" element={<SchedulerSettings />} />
              <Route path="settings/task-categories" element={<TaskCategoriesSettings />} />
              <Route path="settings/brand-kits" element={<BrandKitsSettings />} />
              <Route path="settings/mail-profiles" element={<MailProfilesSettings />} />
              <Route path="settings/project-statuses" element={<ProjectStatusesSettings />} />
              <Route path="settings/people-labels" element={<ProjectPeopleLabelsSettings />} />
              <Route path="settings/modules" element={<PermGate anyOf={["workspace.admin", "workspace.manage"]}><ModulesSettings /></PermGate>} />
              {/* R268q · Feb 2026 — Organization billing profile (legal entity,
                  GSTIN, bank details, logo + PDF template preset). Auto-fills
                  every quote, invoice and credit note in /app/sales. */}
              <Route path="settings/billing-profile" element={<PermGate anyOf={["workspace.admin", "finance.manage", "invoice.manage"]}><BillingSetup /></PermGate>} />
              <Route path="settings/developer" element={<DeveloperSettings />} />
              <Route path="settings/developer/webhooks" element={<DeveloperWebhooks />} />
              <Route path="settings/developer/docs" element={<DeveloperApiDocs />} />
              {/* Feb 2026 · Top-level /app/developer route (nav item) —
                  legacy /app/settings/developer/* still resolves so
                  bookmarks keep working. */}
              <Route path="developer" element={<DeveloperSettings />} />
              <Route path="developer/webhooks" element={<DeveloperWebhooks />} />
              <Route path="developer/docs" element={<DeveloperApiDocs />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="webmail" element={<Webmail />} />
              <Route path="escalations" element={<Escalations />} />
              <Route path="auto-response" element={<AutoResponse />} />
              <Route path="profile" element={<Profile />} />
              <Route path="security" element={<AccountSecurity />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="raid" element={<Navigate to="/app/log" replace />} />
              <Route path="log" element={<Log />} />
              <Route path="log/dashboard" element={<LogDashboard />} />
              <Route path="seo" element={<SEO />} />
              <Route path="employees" element={<PermGate anyOf={["employee.view", "employee.manage"]}><Employees /></PermGate>} />
              <Route path="employees/dashboard" element={<PermGate anyOf={["employee.view", "employee.manage"]}><EmployeesDashboard /></PermGate>} />
              <Route path="hr/pip" element={<PermGate anyOf={["employee.manage", "hr.manage"]}><PIPAdmin /></PermGate>} />
              <Route path="hr/designations" element={<PermGate anyOf={["designation.view", "designation.manage"]}><HRDesignations /></PermGate>} />
              <Route path="hr/job-duties" element={<JobDutiesPage />} />
              <Route path="hr/profile-edits" element={<HRProfileEditsAdmin />} />
              <Route path="me/job-duties" element={<JobDutiesPage selfMode />} />
              <Route path="org-chart" element={<PermGate anyOf={["employee.view", "employee.manage", "department.manage"]}><OrgChart /></PermGate>} />
              <Route path="now" element={<NowDashboard />} />
              <Route path="employees/:eid" element={<PermGate anyOf={["employee.view", "employee.manage"]}><EmployeeDetail /></PermGate>} />
              <Route path="leads" element={<PermGate anyOf={["lead.view"]}><Leads /></PermGate>} />
              <Route path="leads/import-history" element={<PermGate anyOf={["lead.manage"]}><LeadsImportHistory /></PermGate>} />
              <Route path="leads/merge/:sessionId" element={<PermGate anyOf={["lead.manage"]}><LeadsMergeReview /></PermGate>} />
              {/* Feb 2026 · Form builder (CRM lead intake) */}
              <Route path="forms" element={<PermGate anyOf={["lead.view", "lead.manage"]}><FormsPage /></PermGate>} />
              <Route path="forms/merge-queue" element={<PermGate anyOf={["lead.manage"]}><FormMergeQueue /></PermGate>} />
              <Route path="forms/:formId" element={<PermGate anyOf={["lead.manage"]}><FormBuilder /></PermGate>} />
              <Route path="forms/:formId/merge-queue" element={<PermGate anyOf={["lead.manage"]}><FormMergeQueue /></PermGate>} />
              {/* Feb 2026 · Studio Booking module */}
              <Route path="studio" element={<StudioBooking />} />
              <Route path="studio/scan" element={<StudioScan />} />
              <Route path="enquiries" element={<EnquiriesPage />} />
              <Route path="studio/spaces/:spaceId" element={<StudioSpaceDetail />} />
              <Route path="accounts" element={<PermGate anyOf={["lead.view"]}><Accounts /></PermGate>} />
              <Route path="sales" element={<PermGate anyOf={["quotation.view", "sales_order.view", "invoice.view", "payment.view", "finance.view", "finance.manage"]}><GstSales /></PermGate>} />
              <Route path="sales/gst" element={<Navigate to="/app/sales" replace />} />
              <Route path="sales-legacy" element={<PermGate anyOf={["quotation.view", "sales_order.view", "invoice.view", "payment.view"]}><Sales /></PermGate>} />
              <Route path="sales-cc" element={<PermGate anyOf={["quotation.view", "sales_order.view", "invoice.view", "payment.view"]}><SalesCommandCenter /></PermGate>} />
              <Route path="finance/dunning" element={<PermGate anyOf={["finance.view", "invoice.view", "finance.manage", "invoice.manage"]}><DunningQueue /></PermGate>} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/success" element={<BillingSuccess />} />
            </Route>

            {/* Super Admin */}
            <Route
              path="/super-admin"
              element={
                <AdminRoute>
                  <AdminShell />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="workspaces" element={<AdminWorkspaces />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="payment-gateway" element={<AdminPaymentGateway />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="security" element={<AdminSecurity />} />
              <Route path="waitlist" element={<AdminWaitlist />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
              <Route path="ai-usage" element={<AdminAiUsage />} />
              <Route path="data-integrity" element={<AdminDataIntegrity />} />
              <Route path="perf-stats" element={<AdminPerfStats />} />
              <Route path="chrome-extension" element={<AdminChromeExtension />} />
            </Route>

            {/* Chrome extension auth handshake (public route — the page handles the sign-in redirect) */}
            <Route path="/ext/authorize" element={<ExtAuthorize />} />
          </Routes>
          <Toaster richColors position="top-right" closeButton />
        </BrowserRouter>
        </EffortFormatProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
